/**
 * Script d'initialisation de l'accessibilité
 * Implémente les exigences: 1.1, 1.2, 3.1, 4.2
 * 
 * Ce script doit être chargé au début de l'application pour configurer
 * toutes les fonctionnalités d'accessibilité de base.
 */

import { AccessibilityUtils, ariaAnnouncer } from './utils/accessibility-utils.js';
// Import accessibility enhancer for future use
// import { accessibilityEnhancer } from './services/accessibility-enhancement-service.js';

/**
 * Configuration globale de l'accessibilité
 */
interface AccessibilityConfig {
  enableSkipLinks: boolean;
  enableAriaAnnouncements: boolean;
  enableKeyboardNavigation: boolean;
  enableHighContrast: boolean;
  enableReducedMotion: boolean;
  enableFocusManagement: boolean;
}

/**
 * Classe principale d'initialisation de l'accessibilité
 */
class AccessibilityInitializer {
  private config: AccessibilityConfig;
  private initialized = false;

  constructor(config: Partial<AccessibilityConfig> = {}) {
    this.config = {
      enableSkipLinks: true,
      enableAriaAnnouncements: true,
      enableKeyboardNavigation: true,
      enableHighContrast: true,
      enableReducedMotion: true,
      enableFocusManagement: true,
      ...config
    };
  }

  /**
   * Initialise toutes les fonctionnalités d'accessibilité
   */
  public init(): void {
    if (this.initialized) {
      console.warn('Accessibility already initialized');
      return;
    }

    console.log('🔧 Initializing accessibility features...');

    try {
      // Appliquer les préférences d'accessibilité système
      if (this.config.enableHighContrast || this.config.enableReducedMotion) {
        AccessibilityUtils.applyAccessibilityPreferences();
      }



      // Améliorer l'accessibilité des formulaires existants
      this.enhanceExistingForms();

      // Améliorer l'accessibilité des boutons existants
      this.enhanceExistingButtons();

      // Configurer la gestion du focus
      if (this.config.enableFocusManagement) {
        this.setupFocusManagement();
      }

      // Configurer les annonces ARIA
      if (this.config.enableAriaAnnouncements) {
        this.setupAriaAnnouncements();
      }

      // Configurer la navigation clavier globale
      if (this.config.enableKeyboardNavigation) {
        this.setupGlobalKeyboardNavigation();
      }

      // Ajouter les styles d'accessibilité
      this.ensureAccessibilityStyles();

      // Configurer les listeners pour les changements dynamiques
      this.setupDynamicContentListeners();

      this.initialized = true;
      console.log('✅ Accessibility features initialized successfully');

      // Annoncer que l'accessibilité est prête
      if (this.config.enableAriaAnnouncements) {
        setTimeout(() => {
          ariaAnnouncer.announce({
            message: 'Page chargée, fonctionnalités d\'accessibilité activées',
            priority: 'polite'
          });
        }, 1000);
      }

    } catch (error) {
      console.error('❌ Failed to initialize accessibility features:', error);
    }
  }





  /**
   * Améliore l'accessibilité des formulaires existants
   */
  private enhanceExistingForms(): void {
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      AccessibilityUtils.enhanceFormAccessibility(form);
    });
  }

  /**
   * Améliore l'accessibilité des boutons existants
   */
  private enhanceExistingButtons(): void {
    const buttons = document.querySelectorAll('button, [role="button"], input[type="button"], input[type="submit"]');
    buttons.forEach(button => {
      AccessibilityUtils.enhanceButtonAccessibility(button as HTMLElement);
    });
  }

  /**
   * Configure la gestion du focus global
   */
  private setupFocusManagement(): void {
    // Gérer le focus lors des changements de page/section
    document.addEventListener('DOMContentLoaded', () => {
      // Focus sur le contenu principal au chargement
      const mainContent = document.querySelector('main, #main-content, .main');
      if (mainContent && !document.activeElement || document.activeElement === document.body) {
        (mainContent as HTMLElement).focus();
      }
    });

    // Gérer le focus lors des navigations internes
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      
      // Si c'est un lien interne, gérer le focus
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        const targetId = target.getAttribute('href')?.substring(1);
        if (targetId) {
          setTimeout(() => {
            const targetElement = document.getElementById(targetId);
            if (targetElement) {
              targetElement.focus();
            }
          }, 100);
        }
      }
    });
  }

  /**
   * Configure les annonces ARIA globales
   */
  private setupAriaAnnouncements(): void {
    // Annoncer les erreurs JavaScript
    window.addEventListener('error', () => {
      ariaAnnouncer.announce({
        message: 'Une erreur est survenue sur la page',
        priority: 'assertive'
      });
    });

    // Annoncer les changements de connectivité
    window.addEventListener('online', () => {
      ariaAnnouncer.announce({
        message: 'Connexion internet rétablie',
        priority: 'polite'
      });
    });

    window.addEventListener('offline', () => {
      ariaAnnouncer.announce({
        message: 'Connexion internet perdue, certaines fonctionnalités peuvent être limitées',
        priority: 'assertive'
      });
    });
  }

  /**
   * Configure la navigation clavier globale
   */
  private setupGlobalKeyboardNavigation(): void {
    // Gérer la navigation par tabulation
    document.addEventListener('keydown', (e) => {
      // Améliorer la visibilité du focus lors de la navigation clavier
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-navigation');
      }
    });

    // Supprimer la classe lors de l'utilisation de la souris
    document.addEventListener('mousedown', () => {
      document.body.classList.remove('keyboard-navigation');
    });

    // Gérer les raccourcis clavier globaux
    document.addEventListener('keydown', (e) => {
      // Alt + 1 : Aller au contenu principal
      if (e.altKey && e.key === '1') {
        e.preventDefault();
        const mainContent = document.querySelector('main, #main-content') as HTMLElement;
        if (mainContent) {
          mainContent.focus();
          mainContent.scrollIntoView({ behavior: 'smooth' });
        }
      }

      // Alt + 2 : Aller au formulaire de recherche
      if (e.altKey && e.key === '2') {
        e.preventDefault();
        const searchForm = document.querySelector('.search-form input, #search-form input') as HTMLElement;
        if (searchForm) {
          searchForm.focus();
          searchForm.scrollIntoView({ behavior: 'smooth' });
        }
      }

      // Alt + 3 : Aller aux résultats
      if (e.altKey && e.key === '3') {
        e.preventDefault();
        const results = document.querySelector('.search-results, #search-results') as HTMLElement;
        if (results) {
          results.focus();
          results.scrollIntoView({ behavior: 'smooth' });
        }
      }

      // Échap : Fermer les overlays/modales
      if (e.key === 'Escape') {
        const activeModal = document.querySelector('.modal.active, .overlay.active');
        if (activeModal) {
          const closeBtn = activeModal.querySelector('.close, .modal-close') as HTMLElement;
          if (closeBtn) {
            closeBtn.click();
          }
        }
      }
    });
  }

  /**
   * S'assure que les styles d'accessibilité sont chargés
   */
  private ensureAccessibilityStyles(): void {
    // Vérifier si les styles d'accessibilité sont déjà chargés
    const existingStyles = document.querySelector('link[href*="accessibility"], style[data-accessibility]');
    
    if (!existingStyles) {
      // Les styles devraient être inclus dans main.css
      // Mais on peut ajouter des styles critiques ici si nécessaire
      const criticalStyles = document.createElement('style');
      criticalStyles.setAttribute('data-accessibility', 'critical');
      criticalStyles.textContent = `
        /* Styles d'accessibilité critiques */
        .sr-only {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }
        
        .skip-link {
          position: absolute;
          top: -40px;
          left: 6px;
          background: #000;
          color: #fff;
          padding: 8px 16px;
          text-decoration: none;
          border-radius: 4px;
          font-weight: 600;
          z-index: 9999;
          transition: top 0.2s;
        }
        
        .skip-link:focus {
          top: 6px;
        }
        
        *:focus {
          outline: 2px solid #2e8b57;
          outline-offset: 2px;
        }
      `;
      
      document.head.appendChild(criticalStyles);
    }
  }

  /**
   * Configure les listeners pour le contenu dynamique
   */
  private setupDynamicContentListeners(): void {
    // Observer les changements dans le DOM pour améliorer automatiquement l'accessibilité
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              
              // Améliorer automatiquement les nouveaux formulaires
              const newForms = element.querySelectorAll ? element.querySelectorAll('form') : [];
              newForms.forEach(form => {
                AccessibilityUtils.enhanceFormAccessibility(form);
              });

              // Améliorer automatiquement les nouveaux boutons
              const newButtons = element.querySelectorAll ? 
                element.querySelectorAll('button, [role="button"]') : [];
              newButtons.forEach(button => {
                AccessibilityUtils.enhanceButtonAccessibility(button as HTMLElement);
              });

              // Si l'élément lui-même est un formulaire ou un bouton
              if (element.tagName === 'FORM') {
                AccessibilityUtils.enhanceFormAccessibility(element as HTMLFormElement);
              }
              
              if (element.tagName === 'BUTTON' || element.getAttribute('role') === 'button') {
                AccessibilityUtils.enhanceButtonAccessibility(element);
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Vérifie si l'accessibilité est initialisée
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Met à jour la configuration
   */
  public updateConfig(newConfig: Partial<AccessibilityConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

/**
 * Instance globale de l'initialisateur d'accessibilité
 */
export const accessibilityInit = new AccessibilityInitializer();

/**
 * Fonction d'initialisation rapide
 */
export function initializeAccessibility(config?: Partial<AccessibilityConfig>): void {
  if (config) {
    accessibilityInit.updateConfig(config);
  }
  accessibilityInit.init();
}

// Auto-initialisation si le DOM est déjà chargé
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    accessibilityInit.init();
  });
} else {
  // DOM déjà chargé
  accessibilityInit.init();
}